import { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsRobot, BsPerson } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

import AgenticPreviewCards from './AgenticPreviewCards';

/**
 * Renders a single chat message bubble.
 */
const MessageBubble = ({ message }) => {
	const isUser = message.role === 'user';

	return (
		<div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
			{!isUser && (
				<div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
					<BsRobot className="h-3 w-3 text-primary-600" />
				</div>
			)}
			<div
				className={`
					max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-line
		${isUser ? 'bg-purple-600 text-white rounded-br-sm' : 'bg-gray-100 text-text rounded-bl-sm'}
				`}
			>
				{message.content}
			</div>
			{isUser && (
				<div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
					<BsPerson className="h-3 w-3 text-bgBase-800" />
				</div>
			)}
		</div>
	);
};

/**
 * Streaming indicator showing the AI is typing.
 */
const StreamingIndicator = ({ content }) => (
	<div className="flex gap-2 justify-start">
		<div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
			<BsRobot className="h-3 w-3 text-primary-600" />
		</div>
		<div className="max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-line bg-gray-100 text-text rounded-bl-sm">
			{content || (
				<span className="flex gap-1 items-center text-bgBase-800">
					<span
						className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
						style={{ animationDelay: '0ms' }}
					/>
					<span
						className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
						style={{ animationDelay: '150ms' }}
					/>
					<span
						className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
						style={{ animationDelay: '300ms' }}
					/>
				</span>
			)}
		</div>
	</div>
);

/**
 * Loading indicator for preview results.
 */
const PreviewLoading = () => (
	<div className="ml-8 flex items-center gap-2 text-sm text-bgBase-800 py-2">
		<div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
		Suche Beispiel-Unternehmen...
	</div>
);

/**
 * Message list for the agentic search chat.
 */
/**
 * Approve/Refine buttons for Maps search (no preview cards).
 */
const ApprovalButtons = ({ onApprove, onRefine, isExecuting }) => (
	<div className="ml-8 flex gap-2 pt-1">
		<Button onClick={onApprove} size="sm" className="flex-1" loading={isExecuting}>
			{isExecuting ? 'Wird gestartet...' : 'Suche starten'}
		</Button>
		<Button onClick={onRefine} disabled={isExecuting} variant="outline" size="sm" className="flex-1">
			Anpassen
		</Button>
	</div>
);

const AgenticChatMessages = ({
	messages,
	isStreaming,
	streamingContent,
	previewResults,
	isLoadingPreview,
	onApprove,
	onRefine,
	isExecuting,
	phase,
}) => {
	const scrollRef = useRef(null);

	// Auto-scroll to bottom on new messages
	useEffect(() => {
		scrollRef.current?.scrollTo({
			top: scrollRef.current.scrollHeight,
			behavior: 'smooth',
		});
	}, [messages, streamingContent, previewResults, isLoadingPreview]);

	return (
		<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
			{messages.map((msg) => (
				<div key={msg.id}>{msg.type === 'text' && <MessageBubble message={msg} />}</div>
			))}

			{isStreaming && <StreamingIndicator content={streamingContent} />}

			{isLoadingPreview && <PreviewLoading />}

			{previewResults && (
				<AgenticPreviewCards
					companies={previewResults}
					onApprove={onApprove}
					onRefine={onRefine}
					isExecuting={isExecuting}
				/>
			)}

			{phase === 'awaiting_approval' && !previewResults && (
				<ApprovalButtons onApprove={onApprove} onRefine={onRefine} isExecuting={isExecuting} />
			)}
		</div>
	);
};

const messagePropType = PropTypes.shape({
	id: PropTypes.string,
	role: PropTypes.oneOf(['user', 'assistant']),
	content: PropTypes.string,
	type: PropTypes.oneOf(['text', 'preview', 'action']),
});

MessageBubble.propTypes = {
	message: messagePropType.isRequired,
};

StreamingIndicator.propTypes = {
	content: PropTypes.string,
};

ApprovalButtons.propTypes = {
	onApprove: PropTypes.func.isRequired,
	onRefine: PropTypes.func.isRequired,
	isExecuting: PropTypes.bool,
};

AgenticChatMessages.propTypes = {
	messages: PropTypes.arrayOf(messagePropType).isRequired,
	isStreaming: PropTypes.bool,
	streamingContent: PropTypes.string,
	previewResults: PropTypes.array,
	isLoadingPreview: PropTypes.bool,
	onApprove: PropTypes.func.isRequired,
	onRefine: PropTypes.func.isRequired,
	isExecuting: PropTypes.bool,
	phase: PropTypes.string,
};

export default AgenticChatMessages;
